import { useEffect, useState } from 'react';
import { getSiteConfig } from '../api/sites';

/**
 * Whether the signed-in user may edit the active org's website
 * (docs/Decisions.md D43), for the nav. Org admins and platform admins always
 * may, so no request is made for them. Anyone else may only through a
 * delegated editor group, which only the server knows, so it is asked once
 * per org per browser session and the answer cached. The server re-checks
 * on every request; this only decides whether to show the link.
 */
const CACHE_PREFIX = 'cap.siteAccess:';

function cached(orgId) {
  try {
    const value = sessionStorage.getItem(CACHE_PREFIX + orgId);
    return value === null ? null : value === '1';
  } catch {
    return null;
  }
}

function remember(orgId, allowed) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + orgId, allowed ? '1' : '0');
  } catch {
    // sessionStorage unavailable
  }
}

export function useSiteAccess(activeOrg, isPlatformAdmin) {
  const orgId = activeOrg?.orgId || null;
  const isOrgAdmin = activeOrg?.role === 'org-admin';
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!orgId) {
      setAllowed(false);
      return undefined;
    }
    if (isOrgAdmin || isPlatformAdmin) {
      setAllowed(true);
      return undefined;
    }
    const known = cached(orgId);
    if (known !== null) {
      setAllowed(known);
      return undefined;
    }
    let live = true;
    getSiteConfig()
      .then(() => {
        remember(orgId, true);
        if (live) setAllowed(true);
      })
      .catch(() => {
        remember(orgId, false);
        if (live) setAllowed(false);
      });
    return () => {
      live = false;
    };
  }, [orgId, isOrgAdmin, isPlatformAdmin]);

  return allowed;
}

export default useSiteAccess;
